import { useEffect, useState } from "react";

// react-router-dom components
import { Link, useNavigate, useParams } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import MuiLink from "@mui/material/Link";

// @mui icons
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadIcon from "@mui/icons-material/Download";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout components
import CommonLayout from "components/CommonLayout";

// Images
import bgImage from "assets/images/background.png";
import product1 from "assets/images/product1.png";
import userPlaceholder from "assets/images/user_placeholder.png";

import {
  Box,
  Button,
  CardMedia,
  FormControl,
  Icon,
  Input,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import useAuth from "hooks/useAuth";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useUpdateProfileMutation } from "services/auth";
import ShowToast from "components/MDToast";
import { isString } from "utils/validations";
import PhoneInput from "react-phone-input-2";
import { isNumber } from "utils/validations";
import { UploadMedia } from "utils/mediaUpload";
import { useUpdateBankDetailsMutation } from "services/auth";
import useTranslation from "hooks/Translation";
import { getFromStorage } from "helpers";
import { STORAGE_KEYS } from "helpers";

const EditProfile = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [updateProfile] = useUpdateProfileMutation();
  const getLang = getFromStorage(STORAGE_KEYS.language);
  const [updateBankAddress] = useUpdateBankDetailsMutation();
  const { id } = useParams();
  const [image, setImage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [document, setDocument] = useState("");
  const [documentId, setDocumentId] = useState(0);
  const [profileId, setProfileId] = useState("");
  const userData = useAuth();

  const handleProfileUpload = async (event, key) => {
    const files = event.target;
    const file = files?.files?.length ? files?.files[0] : "";

    // Check if a file is selected
    if (file) {
      // Check if the selected file is an image
      if (file.type.startsWith("image/")) {
        const res = await UploadMedia(file);
        if (res?.status === 200) {
          if (key === "profile") {
            formik.setFieldValue("profile_picture", res?.data[0]?.media_url);
            setProfileId(res?.data[0]?.id);
          }
        } else {
          ShowToast(res?.message, "error");
        }
      } else {
        // Display an error message for non-image files
        ShowToast("Please select a valid image file (png or jpeg).", "error");
      }
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      full_name: userData?.full_name || "",
      email: userData?.email || "",
      phone_no: userData?.phone_no || "",
      country_code: userData?.country_code || "",
      business_registration: userData?.business_registration?.id || "",
      profile_picture: userData?.profile_picture?.media_url || "",
      industry_type: String(userData?.industry_type) || "",
      business_name: userData?.business_name || "",
      pin_code: userData?.address?.pin_code || "",
      city: userData?.address?.city || "",
      business_address: userData?.address?.business_address || "",
      ac_number: userData?.bank?.ac_number || "",
      ac_holder_name: userData?.bank?.ac_holder_name || "",
      swift_code: userData?.bank?.swift_code || "",
      bank_name: userData?.bank?.bank_name || "",
      branch_name: userData?.bank?.branch_name || "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(t.error.emailReq)
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          t.error.validEmail
        ),
      full_name: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),

      industry_type: Yup.string().required(t.error.required),
      // dob: Yup.string().required(translation.error.dobReq),
      business_name: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),

      pin_code: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),
      city: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),

      business_address: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),

      ac_number: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),
      ac_holder_name: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),
      swift_code: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),
      bank_name: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),
      branch_name: Yup.string()
        .required(t.error.required)
        .min(2, t.error.minValidation)
        .max(80, t.error.maxValidation),
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);

      // setLoading(true);

      if (!profileId) {
        ShowToast(t.error.uploadImage, "error");
        return;
      }

      let body = {
        email: values.email,
        phone_no: values.phone_no,
        country_code: values.country_code,
        profile_picture: profileId,
        business_name: values.business_name,
        full_name: values.full_name,
        industry_type: Number(values.industry_type),

        business_registration: values?.business_registration,
      };

      try {
        const response = await updateProfile(body).unwrap();
        if (response?.status === 200) {
          handleUpdateBankAddress();
          ShowToast(t.error.profileUpdate);
          navigate("/my-account");
        }
      } catch (error) {
        ShowToast(error?.data?.message || "", "error");
      }
    },
  });

  const handleUpdateBankAddress = async () => {
    const body = {
      business_address: formik?.values?.business_address,
      pin_code: formik?.values.pin_code,
      city: formik?.values.city,
      ac_number: formik?.values.ac_number,
      ac_holder_name: formik?.values.ac_holder_name,
      swift_code: formik?.values.swift_code,
      bank_name: formik?.values.bank_name,
      branch_name: formik?.values.branch_name,
    };
    try {
      const response = await updateBankAddress(body).unwrap();
      if (response?.status === 200) {
      }
    } catch (error) {
      ShowToast(error?.data?.message || "", "error");
    }
  };

  const handleIndustryChange = (event) => {
    formik.setFieldValue("industry_type", event.target.value);
  };

  useEffect(() => {
    setProfileId(userData?.profile_picture?.id);
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card sx={{ marginTop: 10, marginBottom: 5 }}>
        <MDBox
          // variant="gradient"
          bgColor="theme-1"
          borderRadius="lg"
          coloredShadow="success"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          sx={{ display: "flex", justifyContent: "space-between" }}
          // textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {t.auth.editProfile}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {/* <Typography className="custom_label">Image</Typography> */}
                {formik.values.profile_picture ? (
                  <div className="upload_image_preview">
                    <CardMedia
                      component="img"
                      image={formik.values.profile_picture}
                      alt="photo"
                    />
                    <CancelIcon
                      onClick={() => {
                        formik.setFieldValue("profile_picture", "");
                      }}
                    />
                  </div>
                ) : (
                  <Box className="upload_image">
                    <label htmlFor="icon-button-file">
                      <Input
                        sx={{ display: "none" }}
                        id="icon-button-file"
                        type="file"
                        inputProps={{
                          accept: ".png, .jpg, .jpeg, .svg",
                        }}
                        onChange={(e) => handleProfileUpload(e, "profile")}
                      />

                      <Button component="span" className="upload_image_btn">
                        <img src={userPlaceholder} alt="" />
                        <CameraAltIcon />
                      </Button>
                    </label>
                  </Box>
                )}
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.error.fullName}
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.error.fullName}
                    variant="outlined"
                    fullWidth
                    name="full_name"
                    id="full_name"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.full_name}
                    helperText={
                      formik.touched.full_name && formik.errors.full_name
                    }
                    error={formik.touched.full_name && formik.errors.full_name}
                  />
                </MDBox>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.error.businessName}
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.error.businessName}
                    variant="outlined"
                    fullWidth
                    name="business_name"
                    id="business_name"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.business_name}
                    helperText={
                      formik.touched.business_name &&
                      formik.errors.business_name
                    }
                    error={
                      formik.touched.business_name &&
                      formik.errors.business_name
                    }
                  />
                </MDBox>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDTypography variant="subtitle2" className="custom_label">
                  {t.error.industryType}
                </MDTypography>
                <FormControl fullWidth>
                  <Select
                    className="select_div"
                    labelId="demo-simple-select-label"
                    id="demo-simple-select"
                    displayEmpty
                    value={formik.values.industry_type}
                    onChange={handleIndustryChange}
                    error={
                      formik.touched.industry_type &&
                      formik.errors.industry_type
                    }
                    sx={{
                      border:
                        formik.touched.industry_type &&
                        formik.errors.industry_type
                          ? "1px solid red"
                          : "1px solid grey", // Add a custom border color
                      borderRadius: "6px", // Optional: Add border radius for styling
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "grey", // Adjust border color for the outlined variant
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "grey", // Border color on hover
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "grey", // Border color when focused
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      {t.error.select}
                    </MenuItem>

                    <MenuItem value={"1"}>{t.main.retailer}</MenuItem>
                    <MenuItem value={"2"}>{t.main.wholeSaler}</MenuItem>
                    <MenuItem value={"3"}>{t.main.distributor}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.address}
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.address}
                    variant="outlined"
                    fullWidth
                    name="business_address"
                    id="business_address"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.business_address}
                    helperText={
                      formik.touched.business_address &&
                      formik.errors.business_address
                    }
                    error={
                      formik.touched.business_address &&
                      formik.errors.business_address
                    }
                  />
                </MDBox>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.pinCode} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.pinCode}
                    variant="outlined"
                    fullWidth
                    name="pin_code"
                    id="pin_code"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isNumber(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.pin_code}
                    helperText={
                      formik.touched.pin_code && formik.errors.pin_code
                    }
                    error={formik.touched.pin_code && formik.errors.pin_code}
                  />
                </MDBox>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.city} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.city}
                    variant="outlined"
                    fullWidth
                    name="city"
                    id="city"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.city}
                    helperText={formik.touched.city && formik.errors.city}
                    error={formik.touched.city && formik.errors.city}
                  />
                </MDBox>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.email}
                  </MDTypography>
                  <div style={{ position: "relative" }}>
                    <MDInput
                      type="text"
                      placeholder={t.main.email}
                      variant="outlined"
                      fullWidth
                      name="email"
                      id="email"
                      disabled
                      onChange={(val) => {
                        if (
                          val.target.value === " " ||
                          val.target.value === "."
                        ) {
                        } else {
                          formik.handleChange(val);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      helperText={formik.touched.email && formik.errors.email}
                      error={formik.touched.email && formik.errors.email}
                    />
                    <div
                      className="verified"
                      style={{
                        position: "absolute",
                        ...(getLang === "ar"
                          ? { left: "10px" }
                          : { right: "10px" }),
                      }}
                    >
                      <CheckCircleOutlineIcon />
                    </div>
                  </div>
                </MDBox>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.auth.phone}
                  </MDTypography>
                  <div style={{ position: "relative" }}>
                    <PhoneInput
                      country={"ae"}
                      placeholder="0 (000) 000-000"
                      enableSearch={false}
                      id="phone"
                      name="phone"
                      inputStyle={{
                        width: "100%",
                        backgroundColor: "transparent",
                        height: "45px",
                        direction: getLang === "ar" ? "rtl" : "ltr",
                        paddingRight: getLang === "ar" ? "80px" : "0px",
                      }}
                      containerClass={`phone-input-container ${
                        formik.touched.phone_no && formik.errors.phone_no
                          ? "phone-input-error"
                          : ""
                      }`}
                      buttonStyle={{
                        marginRight: getLang === "ar" ? "40px" : "0px",
                      }}
                      // onChange={(phone, country) =>
                      //   handleChangePhone(phone, country)
                      // }
                      onBlur={formik.handleBlur}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault(); // Prevent default behavior (form submission)
                          formik.handleSubmit(); // Manually submit the form
                        }
                      }}
                      value={
                        formik.values.country_code + formik.values.phone_no
                      }
                    />

                    {/* <div className="verified">
                      <CheckCircleOutlineIcon />
                    </div> */}
                  </div>
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDTypography variant="h6">{t.main.bankDetails}</MDTypography>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.bankName} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.bankName}
                    variant="outlined"
                    fullWidth
                    name="bank_name"
                    id="bank_name"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.bank_name}
                    helperText={
                      formik.touched.bank_name && formik.errors.bank_name
                    }
                    error={formik.touched.bank_name && formik.errors.bank_name}
                  />
                </MDBox>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.bankBranch} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.bankBranch}
                    variant="outlined"
                    fullWidth
                    name="branch_name"
                    id="branch_name"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.branch_name}
                    helperText={
                      formik.touched.branch_name && formik.errors.branch_name
                    }
                    error={
                      formik.touched.branch_name && formik.errors.branch_name
                    }
                  />
                </MDBox>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.holder} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.holder}
                    variant="outlined"
                    fullWidth
                    name="ac_holder_name"
                    id="ac_holder_name"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isString(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.ac_holder_name}
                    helperText={
                      formik.touched.ac_holder_name &&
                      formik.errors.ac_holder_name
                    }
                    error={
                      formik.touched.ac_holder_name &&
                      formik.errors.ac_holder_name
                    }
                  />
                </MDBox>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.accNumber} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.accNumber}
                    variant="outlined"
                    fullWidth
                    name="ac_number"
                    id="ac_number"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isNumber(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.ac_number}
                    helperText={
                      formik.touched.ac_number && formik.errors.ac_number
                    }
                    error={formik.touched.ac_number && formik.errors.ac_number}
                  />
                </MDBox>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <MDBox>
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.swift} *
                  </MDTypography>
                  <MDInput
                    type="text"
                    placeholder={t.main.swift}
                    variant="outlined"
                    fullWidth
                    name="swift_code"
                    id="swift_code"
                    onChange={(val) => {
                      if (
                        val.target.value === " " ||
                        val.target.value === "."
                      ) {
                      } else if (isNumber(val.target.value)) {
                        formik.handleChange(val);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.swift_code}
                    helperText={
                      formik.touched.swift_code && formik.errors.swift_code
                    }
                    error={
                      formik.touched.swift_code && formik.errors.swift_code
                    }
                  />
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDTypography variant="h6">{t.main.businessReq}</MDTypography>
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={4}>
                <Box
                  mt={1}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    padding: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    window.open(
                      userData?.business_registration?.media_url,
                      "_blank"
                    )
                  }
                >
                  <MDTypography variant="subtitle2" className="custom_label">
                    {t.main.downloadPdf}
                  </MDTypography>
                  <Button
                    sx={{
                      minWidth: "unset",
                      padding: 0,
                      marginLeft: 2,
                    }}
                  >
                    <DownloadIcon />
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <MDButton
              // onClick={() => navigate("/dashboard")}
              variant="gradient"
              color="success"
              type="submit"
              sx={{ marginTop: 4 }}
            >
              {t.main.save}
            </MDButton>
          </form>
        </MDBox>
      </Card>
    </DashboardLayout>
  );
};

export default EditProfile;
